import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import StarIcon from "@mui/icons-material/Star";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Card from "../../components/common/Card";
import MuiTable from "../../components/common/MuiTable";
import { Column } from "../../utils/constants";
import {
  ReviewModerationService,
  PendingReview,
} from "../../api/reviewModerationService";
import moment from "moment";

const ReviewModeration = () => {
  const { t } = useTranslation();
  const [dataTable, setDataTable] = useState<PendingReview[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Reject modal state
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [rejectionNote, setRejectionNote] = useState("");
  const [isSubmittingReject, setIsSubmittingReject] = useState(false);

  // Approve confirmation state
  const [isApprovingId, setIsApprovingId] = useState<number | null>(null);

  useEffect(() => {
    fetchPendingReviews();
  }, [page, rowsPerPage]);

  const fetchPendingReviews = async () => {
    try {
      setLoading(true);
      const response = await ReviewModerationService.getPendingReviews(
        page,
        rowsPerPage
      );

      // Access data via response.data (RestPagingResponse structure)
      // Structure: { success, code, data: [...], total, currentPage, perPage, ... }
      setDataTable(response.data.data || []);
      setTotalElements(response.data.total || 0);
    } catch (error: any) {
      console.error("Error fetching pending reviews:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to load pending reviews. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId: number) => {
    try {
      setIsApprovingId(reviewId);
      const response = await ReviewModerationService.approveReview(
        reviewId,
        "Approved"
      );

      const result = response.data;

      toast.success(result.message || "Review approved successfully!");

      // Refresh the list
      await fetchPendingReviews();
    } catch (error: any) {
      console.error("Error approving review:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to approve review. Please try again."
      );
    } finally {
      setIsApprovingId(null);
    }
  };

  const handleOpenRejectModal = (reviewId: number) => {
    setSelectedReviewId(reviewId);
    setRejectionNote("");
    setOpenRejectModal(true);
  };

  const handleCloseRejectModal = () => {
    setOpenRejectModal(false);
    setSelectedReviewId(null);
    setRejectionNote("");
  };

  const handleRejectSubmit = async () => {
    if (!rejectionNote.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    if (!selectedReviewId) return;

    try {
      setIsSubmittingReject(true);
      const response = await ReviewModerationService.rejectReview(
        selectedReviewId,
        rejectionNote
      );

      const result = response.data;

      toast.success(result.message || "Review rejected successfully!");

      handleCloseRejectModal();

      // Refresh the list
      await fetchPendingReviews();
    } catch (error: any) {
      console.error("Error rejecting review:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to reject review. Please try again."
      );
    } finally {
      setIsSubmittingReject(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderRatingStars = (score: number) => {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
        {[...Array(5)].map((_, index) => (
          <StarIcon
            key={index}
            sx={{
              fontSize: 16,
              color: index < score ? "#FFD700" : "#E0E0E0",
            }}
          />
        ))}
        <Typography variant="body2" sx={{ ml: 0.5 }}>
          ({score})
        </Typography>
      </Box>
    );
  };

  const header: Column[] = [
    {
      id: "id",
      label: "Review ID",
      minWidth: 50,
      align: "center",
    },
    {
      id: "salon_name",
      label: "Salon Name",
      minWidth: 150,
      align: "left",
    },
    {
      id: "reviewer_name",
      label: "Reviewer",
      minWidth: 120,
      align: "left",
    },
    {
      id: "score_outlet",
      label: "Rating",
      minWidth: 120,
      align: "center",
      format: (value: number) => renderRatingStars(value),
    },
    {
      id: "review_text",
      label: "Review Text",
      minWidth: 250,
      align: "left",
      format: (value: string) => {
        const maxLength = 100;
        if (value && value.length > maxLength) {
          return (
            <Tooltip title={value} placement="top">
              <Typography variant="body2" sx={{ cursor: "pointer" }}>
                {value.substring(0, maxLength)}...
              </Typography>
            </Tooltip>
          );
        }
        return <Typography variant="body2">{value || "No text"}</Typography>;
      },
    },
    {
      id: "review_date",
      label: "Review Date",
      minWidth: 140,
      align: "center",
      format: (value: string) => {
        return <div>{moment(value).format("YYYY-MM-DD HH:mm")}</div>;
      },
    },
    {
      id: "action",
      label: "Actions",
      minWidth: 150,
      align: "center",
      format: (value: any, row: PendingReview) => {
        return (
          <Box display="flex" gap={1} justifyContent="center">
            <Tooltip title="Approve Review">
              <span>
                <IconButton
                  color="success"
                  onClick={() => handleApprove(row.id)}
                  disabled={isApprovingId === row.id}
                  size="small"
                >
                  {isApprovingId === row.id ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CheckCircleIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reject Review">
              <IconButton
                color="error"
                onClick={() => handleOpenRejectModal(row.id)}
                disabled={isApprovingId === row.id}
                size="small"
              >
                <CancelIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <div style={{ width: "100%", padding: "20px" }}>
      <Card>
        <Box sx={{ padding: 3 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
            Review Moderation - Pending Reviews
          </Typography>

          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <MuiTable
              header={header}
              data={dataTable}
              page={page}
              rowsPerPage={rowsPerPage}
              totalElements={totalElements}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Box>
      </Card>

      {/* Reject Modal */}
      <Dialog
        open={openRejectModal}
        onClose={handleCloseRejectModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Review</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please provide a reason for rejecting this review. This note will be
            recorded for audit purposes.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={rejectionNote}
            onChange={(e) => setRejectionNote(e.target.value)}
            placeholder="e.g., Contains inappropriate content, spam, violates guidelines..."
            required
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseRejectModal}
            disabled={isSubmittingReject}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectSubmit}
            disabled={isSubmittingReject || !rejectionNote.trim()}
            variant="contained"
            color="error"
          >
            {isSubmittingReject ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Rejecting...
              </>
            ) : (
              "Reject Review"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReviewModeration;
